import AppContext from 'App/AppContext';
import { IPSpinner } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall } from 'globalStyles';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { ProcessingActivityLinkType } from 'models/dto/processingActivity/processingActivityLinkDTO';
import ProcessingActivity from 'models/processingActivity';
import ResourceLink from 'models/resourceLink';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiAddLinkToProcessingActivity,
  apiCreateLink,
  apiRemoveLinkFromProcessingActivity,
  apiUpdateLinkToProcessingActivity,
} from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import AddNewLink from '../AddNewLink';
import LinkList from '../LinkList';

interface IProcessingActivityLinkListProps {
  processingActivity: ProcessingActivity | undefined;
  authSchemaId: number | undefined;
  navigateExternal?: boolean;
  onPinLink: (item: ResourceLink, pinned: boolean) => void;
  onRemove: (item: ResourceLink) => void;
  onAdd: (items: ResourceLink[]) => void;
}

export const ProcessingActivityLinkList = (props: IProcessingActivityLinkListProps) => {
  const { t } = useTranslation(['translation', 'library']);
  const appContext = useContext(AppContext);
  const [readonly] = useState<boolean>(
    !hasUserDataPermission(appContext, [props.authSchemaId], AuthSchemaLineOperation.Update),
  );
  const isLoading = props.processingActivity === undefined;
  const showLinks = props.processingActivity?.resourceLinks ?? [];

  const showFailureNotification = (message: string) => {
    appContext.showNotification(message, true);
  };

  const addLinks = async (items: ResourceLink[]) => {
    if (!props.processingActivity) return;
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      for (let idx = 0; idx < items.length; idx++) {
        const item = items[idx];
        if (item.linkId <= 0) {
          //create Link and then add the connection
          await appContext.globalDataCache.lists.getItems();
          const newLinkCreated = await apiCreateLink(item, accessToken, appContext.globalDataCache);
          await apiAddLinkToProcessingActivity(
            newLinkCreated.linkId,
            props.processingActivity.processingActivityId,
            newLinkCreated.linkTypeId,
            newLinkCreated.linkURLFragment,
            accessToken,
          );
          items[idx] = newLinkCreated;
        } else {
          //existing link just add the connection
          await apiAddLinkToProcessingActivity(
            item.linkId,
            props.processingActivity.processingActivityId,
            item.linkTypeId,
            item.linkURLFragment,
            accessToken,
          );
        }
        //add to pinned items when it was already pinned
        if (item.pinned) {
          props.onPinLink(item, true);
        }
      }

      props.onAdd(items);
    } catch {
      showFailureNotification(t('library:LinkComponents.ErrorMessageAdd'));
    }
  };

  const onPinLink = async (item: ResourceLink, pinned: boolean) => {
    if (!props.processingActivity) return;
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      item.linkTypeId = pinned ? ProcessingActivityLinkType.Pinned : ProcessingActivityLinkType.Default;
      item.pinned = pinned;
      props.onPinLink(item, pinned);
      apiUpdateLinkToProcessingActivity(
        item.linkId,
        props.processingActivity.processingActivityId,
        item.linkTypeId,
        item.linkURLFragment,
        accessToken,
      );
    } catch (err) {
      appContext.setError(err);
    }
  };

  const removeLink = async (item: ResourceLink) => {
    if (!props.processingActivity) return;
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiRemoveLinkFromProcessingActivity(item, props.processingActivity.processingActivityId, accessToken);
      if (item.pinned) {
        props.onPinLink(item, false);
      }
      props.onRemove(item);
    } catch {
      showFailureNotification(t('library:LinkComponents.ErrorMessageRemove'));
    } finally {
    }
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  } else if (props.processingActivity === undefined) {
    return null;
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {/* Render links and other components here */}
      {!readonly && (
        <IPStack.Item>
          <AddNewLink
            addLinks={addLinks}
            existingLinks={showLinks}
            allowCustomLists={true}
            allowDocumentLibraries={true}
            allowWebURLs={true}
            allowSitePages={true}
            disabled={isLoading}
            entityContext={true}
            showCategoryContents={true}
          />
        </IPStack.Item>
      )}
      <LinkList
        items={showLinks}
        onDelete={removeLink}
        onPin={onPinLink}
        navigateExternal={props.navigateExternal}
        readonly={readonly}
      />
    </IPStack>
  );
};
